import { put, all, call, takeLatest } from 'redux-saga/effects';
import { success, error } from 'react-toastify-redux';

import {
  getSpaces as getSpacesApi,
  createSpace as createSpaceApi,
  updateSpace as updateSpaceApi,
  deleteSpace as deleteSpaceApi,
  saveAssessment as saveAssessmentApi,
  getAssessments as getAssessmentsApi,
  getBhpsFeatures as getBhpsFeaturesApi,
  updateBhpsFeatures as updateBhpsFeaturesApi,
  getPopulation as getPopulationApi,
  savePopulation as savePopulationApi,
  deletePopulation as deletePopulationApi,
} from '../services';

import actions from './actions';
import types from './types';

// ── localStorage helpers for offline/fallback space storage ──

function getLocalStorageKey(projectId) {
  return `brainHealth_spaces_${projectId}`;
}

function loadLocalSpaces(projectId) {
  try {
    const raw = localStorage.getItem(getLocalStorageKey(projectId));
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function saveLocalSpaces(projectId, spaces) {
  try {
    localStorage.setItem(getLocalStorageKey(projectId), JSON.stringify(spaces));
  } catch (e) {
    // ignore
  }
}

function generateId() {
  return 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// ── Spaces ──

function* getSpacesProcess(action) {
  try {
    const projectId = action.payload;
    const response = yield call(getSpacesApi, projectId);
    if (response && !response.error && !response.code) {
      const spaces = Array.isArray(response) ? response : Object.values(response);
      // Sync API data to localStorage so it's available offline
      saveLocalSpaces(projectId, spaces);
      yield put(actions.getSpacesSuccess(spaces));
      return;
    }
  } catch (err) {
    // API failed — fall through to localStorage
  }
  // Fallback: load from localStorage
  const projectId = action.payload;
  const localSpaces = loadLocalSpaces(projectId);
  yield put(actions.getSpacesSuccess(localSpaces));
}

function* createSpaceProcess(action) {
  const { projectId, spaceData } = action.payload;

  // Try API first
  try {
    const response = yield call(createSpaceApi, projectId, spaceData);
    if (response && !response.error) {
      // Sync to localStorage so data is available offline
      const existing = loadLocalSpaces(projectId);
      existing.push(response);
      saveLocalSpaces(projectId, existing);
      yield put(actions.createSpaceSuccess(response));
      yield put(success('Space created'));
      yield put(actions.getSpaces(projectId));
      return;
    }
  } catch (err) {
    // API failed — save locally
  }

  // Fallback: save to localStorage
  const newSpace = { ...spaceData, id: generateId(), projectId };
  const existing = loadLocalSpaces(projectId);
  existing.push(newSpace);
  saveLocalSpaces(projectId, existing);
  yield put(actions.createSpaceSuccess(newSpace));
  yield put(success('Space saved locally'));
  yield put(actions.getSpacesSuccess(existing));
}

function* updateSpaceProcess(action) {
  const { spaceId, spaceData } = action.payload;

  // Try API first
  try {
    const response = yield call(updateSpaceApi, spaceId, spaceData);
    if (response && !response.error) {
      yield put(actions.updateSpaceSuccess(response));
      yield put(success('Space updated'));
      return;
    }
  } catch (err) {
    // API failed — update locally
  }

  // Fallback: update in localStorage (search all projects)
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('brainHealth_spaces_')) {
      const spaces = JSON.parse(localStorage.getItem(key) || '[]');
      const idx = spaces.findIndex(s => s.id === spaceId);
      if (idx !== -1) {
        spaces[idx] = { ...spaces[idx], ...spaceData };
        localStorage.setItem(key, JSON.stringify(spaces));
        yield put(actions.updateSpaceSuccess(spaces[idx]));
        yield put(success('Space updated locally'));
        const pid = key.replace('brainHealth_spaces_', '');
        yield put(actions.getSpacesSuccess(spaces));
        return;
      }
    }
  }
  yield put(actions.updateSpaceFailure('Space not found'));
}

function* deleteSpaceProcess(action) {
  const { spaceId, projectId } = action.payload;

  // Try API first
  try {
    yield call(deleteSpaceApi, spaceId);
    yield put(actions.deleteSpaceSuccess());
    yield put(success('Space deleted'));
    yield put(actions.getSpaces(projectId));
    return;
  } catch (err) {
    // API failed — delete locally
  }

  // Fallback: delete from localStorage
  const spaces = loadLocalSpaces(projectId).filter(s => s.id !== spaceId);
  saveLocalSpaces(projectId, spaces);
  yield put(actions.deleteSpaceSuccess());
  yield put(success('Space deleted'));
  yield put(actions.getSpacesSuccess(spaces));
}

// ── Assessments ──

function* saveAssessmentProcess(action) {
  try {
    const { projectId, assessmentData } = action.payload;
    const response = yield call(saveAssessmentApi, projectId, assessmentData);
    if (response && response.error) {
      yield put(actions.saveAssessmentFailure(response.error.message));
      return;
    }
    yield put(actions.saveAssessmentSuccess(response));
    yield put(success('Assessment saved'));
  } catch (err) {
    yield put(actions.saveAssessmentFailure(err));
  }
}

function* getAssessmentsProcess(action) {
  try {
    const projectId = action.payload;
    const response = yield call(getAssessmentsApi, projectId);
    yield put(actions.getAssessmentsSuccess(Array.isArray(response) ? response : []));
  } catch (err) {
    yield put(actions.getAssessmentsFailure(err));
  }
}

// ── BHPS Features ──

function* getBhpsFeaturesProcess(action) {
  try {
    const spaceId = action.payload;
    const response = yield call(getBhpsFeaturesApi, spaceId);
    yield put(actions.getBhpsFeaturesSuccess(response || {}));
  } catch (err) {
    yield put(actions.getBhpsFeaturesFailure(err));
  }
}

function* updateBhpsFeaturesProcess(action) {
  try {
    const { spaceId, features } = action.payload;
    yield call(updateBhpsFeaturesApi, spaceId, features);
    yield put(actions.updateBhpsFeaturesSuccess());
    yield put(success('BHPS features updated'));
  } catch (err) {
    yield put(actions.updateBhpsFeaturesFailure(err));
  }
}

// ── Population (localStorage fallback) ──

function getPopulationLS(projectId) {
  try { return JSON.parse(localStorage.getItem('brainHealth_population_' + projectId) || '[]'); }
  catch { return []; }
}

function savePopulationLS(projectId, items) {
  localStorage.setItem('brainHealth_population_' + projectId, JSON.stringify(items));
}

function* getPopulationProcess(action) {
  try {
    const projectId = action.payload;
    let data;
    try {
      const response = yield call(getPopulationApi, projectId);
      data = Array.isArray(response) ? response : Object.values(response);
      if (!data || data.length === 0) throw new Error('empty');
    } catch {
      data = getPopulationLS(projectId);
    }
    yield put(actions.getPopulationSuccess(data));
  } catch (err) {
    yield put(actions.getPopulationFailure(err));
  }
}

function* savePopulationProcess(action) {
  try {
    const { projectId, populationData } = action.payload;

    // Always save to localStorage immediately
    const existing = getPopulationLS(projectId);
    const idx = existing.findIndex(
      p => p.category === populationData.category && p.roleLevel === populationData.roleLevel
    );
    if (idx !== -1) {
      existing[idx] = { ...existing[idx], ...populationData, id: existing[idx].id };
    } else {
      existing.push({ ...populationData, id: 'pop_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6) });
    }
    savePopulationLS(projectId, existing);

    // Try API too (may fail)
    try {
      yield call(savePopulationApi, projectId, populationData);
    } catch {
      // API failed, localStorage is the source of truth
    }

    yield put(actions.savePopulationSuccess(existing));
    yield put(success('Population data saved'));
    yield put(actions.getPopulation(projectId));
  } catch (err) {
    yield put(actions.savePopulationFailure(err));
  }
}

function* deletePopulationProcess(action) {
  try {
    yield call(deletePopulationApi, action.payload);
    yield put(actions.deletePopulationSuccess());
  } catch (err) {
    yield put(actions.deletePopulationFailure(err));
  }
}

export default function* watchBrainHealth() {
  yield all([
    takeLatest(types.GET_SPACES, getSpacesProcess),
    takeLatest(types.CREATE_SPACE, createSpaceProcess),
    takeLatest(types.UPDATE_SPACE, updateSpaceProcess),
    takeLatest(types.DELETE_SPACE, deleteSpaceProcess),
    takeLatest(types.SAVE_ASSESSMENT, saveAssessmentProcess),
    takeLatest(types.GET_ASSESSMENTS, getAssessmentsProcess),
    takeLatest(types.GET_BHPS_FEATURES, getBhpsFeaturesProcess),
    takeLatest(types.UPDATE_BHPS_FEATURES, updateBhpsFeaturesProcess),
    takeLatest(types.GET_POPULATION, getPopulationProcess),
    takeLatest(types.SAVE_POPULATION, savePopulationProcess),
    takeLatest(types.DELETE_POPULATION, deletePopulationProcess),
  ]);
}
